import { watch } from "vue";
import { storeToRefs } from "pinia";
import { Quasar, QuasarLanguage } from "quasar";
import langDe from "quasar/lang/de";
import langEn from "quasar/lang/en-GB";
import langEs from "quasar/lang/es";
import langFr from "quasar/lang/fr";
import langIt from "quasar/lang/it";
import langJa from "quasar/lang/ja";
import langZh from "quasar/lang/zh-CN";
import { getI18n } from "@/shared/i18n/i18n";
import { useLanguageStore } from "@/shared/store/language-store";

const QUASAR_LANGUAGES: Record<string, QuasarLanguage> = {
    de: langDe,
    en: langEn,
    fr: langFr,
    es: langEs,
    it: langIt,
    ja: langJa,
    zh: langZh,
};

/**
 * Composable that keeps i18n and Quasar language settings
 * reactively synchronized with the Pinia language store.
 *
 * Call once in the root App component (or equivalent setup context).
 */
export function useLanguageSync() {
    const languageStore = useLanguageStore();
    const { uiLocale, fallbackLocales } = storeToRefs(languageStore);

    // Sync i18n + Quasar whenever uiLocale changes
    watch(
        uiLocale,
        (locale) => {
            syncI18n(locale, fallbackLocales.value);
            syncQuasar(locale);
        },
        { immediate: true }
    );

    // Also react to fallback changes
    watch(fallbackLocales, (fallbacks) => {
        syncI18n(uiLocale.value, fallbacks);
    });
}

function syncI18n(locale: string, fallbacks: string[]) {
    const i18n = getI18n().global;
    i18n.locale.value = locale;
    i18n.fallbackLocale.value = fallbacks;
}

function syncQuasar(locale: string) {
    const quasarLang = QUASAR_LANGUAGES[locale] ?? QUASAR_LANGUAGES["en"];
    Quasar.lang.set(quasarLang);
}
